// src/services/offline/conflictResolver.service.ts
import { indexedDBService } from './indexedDB.service';
import { syncQueueService } from './syncQueue.service';
import { SyncQueueItem } from '../../types/offline.types';

export type ConflictResolution = 'local' | 'server' | 'merge';

export interface ConflictInfo {
  item: SyncQueueItem;
  localData: any;
  serverData: any;
  differences: Array<{
    field: string;
    localValue: any;
    serverValue: any;
  }>;
}

class ConflictResolverService {
  /**
   * Get all conflicts
   */
  async getConflicts(): Promise<SyncQueueItem[]> {
    const queue = await syncQueueService.getAll();
    return queue.filter((item) => item.status === 'conflict');
  }

  /**
   * Get conflict details
   */
  async getConflictInfo(conflictId: string): Promise<ConflictInfo | null> {
    const item = await syncQueueService.getItem(conflictId);
    if (!item || item.status !== 'conflict') return null;

    // Get local data
    let localData: any = null;
    switch (item.collection) {
      case 'workloads':
        localData = await indexedDBService.getWorkload(item.documentId);
        break;
      case 'addresses':
        localData = await indexedDBService.getAddress(item.documentId);
        break;
      case 'surveys':
        localData = await indexedDBService.getSurvey(item.documentId);
        break;
      case 'surveyResponses':
        localData = await indexedDBService.getSurveyResponse(item.documentId);
        break;
    }

    // Server data is stored in conflictData
    const serverData = item.conflictData;

    // Calculate differences
    const differences = this.calculateDifferences(localData, serverData);

    return {
      item,
      localData,
      serverData,
      differences,
    };
  }

  /**
   * Calculate differences between local and server data
   */
  private calculateDifferences(
    local: any,
    server: any
  ): Array<{ field: string; localValue: any; serverValue: any }> {
    const differences: Array<{ field: string; localValue: any; serverValue: any }> = [];

    if (!local || !server) return differences;

    const allKeys = new Set([...Object.keys(local), ...Object.keys(server)]);

    for (const key of allKeys) {
      // Skip meta fields
      if (['syncStatus', 'localVersion', 'serverVersion'].includes(key)) continue;

      const localValue = local[key];
      const serverValue = server[key];

      if (JSON.stringify(localValue) !== JSON.stringify(serverValue)) {
        differences.push({
          field: key,
          localValue,
          serverValue,
        });
      }
    }

    return differences;
  }

  /**
   * Resolve a conflict
   */
  async resolve(
    conflictId: string,
    resolution: ConflictResolution,
    mergedData?: any
  ): Promise<void> {
    const item = await syncQueueService.getItem(conflictId);
    if (!item || item.status !== 'conflict') {
      throw new Error('Conflict not found');
    }

    let dataToUse: any;

    switch (resolution) {
      case 'local':
        // Use local data - just retry the sync
        dataToUse = item.data;
        break;
      case 'server':
        // Use server data - discard local changes
        dataToUse = item.conflictData;
        break;
      case 'merge':
        if (!mergedData) {
          throw new Error('Merged data required for merge resolution');
        }
        dataToUse = mergedData;
        break;
    }

    // Update local store with resolved data
    switch (item.collection) {
      case 'workloads':
        await indexedDBService.saveWorkload(dataToUse, 'pending');
        break;
      case 'addresses':
        await indexedDBService.saveAddress(dataToUse, 'pending');
        break;
      case 'surveys':
        await indexedDBService.saveSurvey(dataToUse, 'pending');
        break;
      case 'surveyResponses':
        await indexedDBService.saveSurveyResponse(dataToUse, 'pending');
        break;
    }

    if (resolution === 'server') {
      // Server wins, no need to sync back
      await syncQueueService.remove(conflictId);
    } else {
      // Retry sync with resolved data
      await syncQueueService.retry(conflictId);
    }
  }

  /**
   * Auto-resolve conflicts based on strategy
   */
  async autoResolve(
    strategy: 'server-wins' | 'local-wins' | 'newest-wins'
  ): Promise<number> {
    const conflicts = await this.getConflicts();
    let resolved = 0;

    for (const conflict of conflicts) {
      try {
        let resolution: ConflictResolution;

        switch (strategy) {
          case 'server-wins':
            resolution = 'server';
            break;
          case 'local-wins':
            resolution = 'local';
            break;
          case 'newest-wins':
            // Compare timestamps
            const localTimestamp = conflict.data?.updatedAt?.toMillis?.() || conflict.timestamp;
            const serverTimestamp = conflict.conflictData?.updatedAt?.toMillis?.() || 0;
            resolution = localTimestamp > serverTimestamp ? 'local' : 'server';
            break;
        }

        await this.resolve(conflict.id, resolution);
        resolved++;
      } catch (error) {
        console.error('Error auto-resolving conflict:', error);
      }
    }

    return resolved;
  }

  /**
   * Get the count of conflicts
   */
  async getConflictCount(): Promise<number> {
    const conflicts = await this.getConflicts();
    return conflicts.length;
  }

  /**
   * Clear all conflicts (discard local changes)
   */
  async clearAllConflicts(): Promise<void> {
    const conflicts = await this.getConflicts();
    for (const conflict of conflicts) {
      await this.resolve(conflict.id, 'server');
    }
  }
}

export const conflictResolverService = new ConflictResolverService();
